import type { ComponentType } from 'react';
import type { WorkQueueCase, StatusMeta, OutgoingAction } from '../../../services/workQueue';
import { ReviewRecordsTask } from './ReviewRecordsTask';
import { ReviewDesignTask } from './ReviewDesignTask';
import { DesignReviewTask } from './DesignReviewTask';
import { SubmitToManufacturerTask } from './SubmitToManufacturerTask';
import { CreateRefinementInManufacturerTask } from './CreateRefinementInManufacturerTask';
import { SubmitDesignPlanTask } from './SubmitDesignPlanTask';
import { SubmitRefinementDesignTask } from './SubmitRefinementDesignTask';
import { RefinementSubmitManufacturingTask } from './RefinementSubmitManufacturingTask';
import { SdiSubmitManufacturingTask } from './SdiSubmitManufacturingTask';
import { CreateSdiTask } from './CreateSdiTask';
import { CreateRdiTask } from './CreateRdiTask';
import { SetupDmTask } from './SetupDmTask';
import { StartMonitoringTask } from './StartMonitoringTask';
import { ScheduleAppointmentTask } from './ScheduleAppointmentTask';
import { ManageTreatmentTask } from './ManageTreatmentTask';
import { ManageVirtualAppointmentsTask } from './ManageVirtualAppointmentsTask';
import { FinalReviewTask } from './final-review/FinalReviewTask';
import { OrthoReviewTask } from './ortho-review/OrthoReviewTask';
import { RefinementOrthoReviewTask } from './ortho-review/RefinementOrthoReviewTask';
import { RefinementGoAheadTask, RetentionGoAheadTask } from './go-ahead/GoAheadTask';
import { WaitingOnManufacturerTask } from './WaitingOnManufacturerTask';
import { RefinementWaitingTask } from './RefinementWaitingTask';
import { RefinementRefusalTask } from './RefinementRefusalTask';
import { RetentionRefusalTask } from './RetentionRefusalTask';
import { RetentionSubmitManufacturingTask } from './RetentionSubmitManufacturingTask';
import { ApprovalReviewTask } from './approval-review/ApprovalReviewTask';
import { SdiReviewTask, RdiReviewTask } from './review-surface/ClinicalDocReviewTask';
import { ViewSubmissionTask } from './submission-progress/ViewSubmissionTask';
import { WaitForDesignTask } from './design-dx/WaitForDesignTask';
import { ReviewDesignFinalizeDxTask } from './design-dx/ReviewDesignFinalizeDxTask';
import { HandBackChip } from './design-dx/HandBackBanner';
import {
    APPROVAL_REVIEW_TASK_ID,
    FINAL_REVIEW_TASK_ID,
    SDI_REVIEW_TASK_ID,
    RDI_REVIEW_TASK_ID
} from '../constants';

export interface TaskPayloadState {
    payload: Record<string, unknown> | null;
    valid: boolean;
    bodyOwnsSubmit?: boolean;
    /** Body has a non-interruptible operation in flight (e.g. uploading files);
     *  the surface locks its "back to queue" control while true. */
    busy?: boolean;
    /** Action id -> hint: the surface footer disables those actions and shows the hint. */
    disabledActions?: Record<string, string>;
    /** Action ids the surface footer leaves out, e.g. an exit that only applies after a hand-back. */
    hiddenActions?: readonly string[];
}

export interface TaskBodyProps {
    workCase: WorkQueueCase;
    statusMeta: StatusMeta;
    onPayloadChange?: (state: TaskPayloadState) => void;
    onTaskComplete?: (action?: OutgoingAction) => void;
    onRegisterBeforeClose?: (handler: (() => Promise<void>) | null) => void;
}

const TASK_REGISTRY: Record<string, ComponentType<TaskBodyProps>> = {
    'view-submission': ViewSubmissionTask,
    'review-records': ReviewRecordsTask,
    'submit-to-manufacturer': SubmitToManufacturerTask,
    'create-refinement-in-manufacturer': CreateRefinementInManufacturerTask,
    'submit-design-plan': SubmitDesignPlanTask,
    'wait-for-design': WaitForDesignTask,
    'review-design-finalize-dx': ReviewDesignFinalizeDxTask,
    'submit-refinement-design': SubmitRefinementDesignTask,
    'review-refinement-design': DesignReviewTask,
    'submit-refinement-manufacturing': RefinementSubmitManufacturingTask,
    'review-refinement': RefinementOrthoReviewTask,
    'create-sdi': CreateSdiTask,
    'create-rdi': CreateRdiTask,
    'create-patient-in-dm': SetupDmTask,
    'start-monitoring': StartMonitoringTask,
    'schedule-in-office-appointment': ScheduleAppointmentTask,
    'view-treatment': ManageTreatmentTask,
    'manage-virtual-appointments': ManageVirtualAppointmentsTask,
    [FINAL_REVIEW_TASK_ID]: FinalReviewTask,
    'ortho-review': OrthoReviewTask,
    'refinement-go-ahead': RefinementGoAheadTask,
    'retention-go-ahead': RetentionGoAheadTask,
    'waiting-on-manufacturer': WaitingOnManufacturerTask,
    'refinement-waiting-on-manufacturer': RefinementWaitingTask,
    'refinement-refusal': RefinementRefusalTask,
    'retention-refusal': RetentionRefusalTask,
    'submit-retention-manufacturing': RetentionSubmitManufacturingTask,
    'submit-post-approval-redesign': ReviewDesignTask,
    'submit-sdi-manufacturing': SdiSubmitManufacturingTask,
    'review-sdi': SdiSubmitManufacturingTask,
    'review-post-approval-redesign': DesignReviewTask,
    [APPROVAL_REVIEW_TASK_ID]: ApprovalReviewTask,
    [SDI_REVIEW_TASK_ID]: SdiReviewTask,
    [RDI_REVIEW_TASK_ID]: RdiReviewTask
};

export function getTaskComponent(taskId: string): ComponentType<TaskBodyProps> | null {
    return TASK_REGISTRY[taskId] ?? null;
}

// Frontend-only hover hint next to the task title in TaskSurface - kept out of
// the engine so the backend stays UI-agnostic.
const TASK_DESCRIPTIONS: Record<string, string> = {
    'view-submission':
        'View-only: the practice is still creating this case. See which sections of their submission are complete and what is still missing.',
    'review-records':
        'Verify the practice-uploaded records are usable. Add extracted STLs or CBCTs here before approving.',
    'submit-to-manufacturer':
        'Spark patients: a guided 3-step wizard creates the case and uploads the photos and scans automatically. Other manufacturers: create the case on the manufacturer site, then enter the Case ID and link to submit for design.',
    'create-refinement-in-manufacturer':
        'Create the refinement case in the manufacturer (Spark) using the patient info, manufacturer link, and files shown here, and flip the Ortho Task Board status. Mark as created to advance the case.',
    'submit-design-plan':
        'Provide treatment design instructions to the manufacturer to begin design, and draft the Dx report alongside them.',
    'wait-for-design':
        'Spark is working on the design. Keep drafting the Dx report meanwhile; when the design arrives, mark the plan received to advance the case to review.',
    'review-design-finalize-dx':
        "Review the manufacturer's design against the submitted instructions and finalize the Dx report. Send to final review once a Dx draft is saved, or reject with new instructions so the manufacturer can rework.",
    'submit-refinement-design':
        'Provide refinement design instructions to the manufacturer to begin design. Past submissions are scoped to the current refinement.',
    'review-refinement-design':
        "Review the manufacturer's refinement design against the submitted instructions. Approve to create RDIs, or reject to send it back for rework.",
    'submit-refinement-manufacturing':
        'Review the finalized refinement design and the RDI for this refinement, then submit the case in the Spark portal. Mark as submitted once created with the manufacturer.',
    'review-refinement':
        "Review the refinement design and the latest RDI on the orthodontist's behalf: approve them for manufacturing, or request a revision with notes back to Clinical. To change the RDI first, open it and create a new version.",
    'create-sdi':
        'Create the start-day instructions (SDI) for the doctor. Submitting the SDI advances the case to DM onboarding.',
    'create-rdi':
        'Create the refinement design instructions (RDI) for the doctor. Submitting the RDI advances the case to manufacturing submission.',
    'create-patient-in-dm':
        'Set up the patient in Dental Monitoring: create their DM record (or link an existing one), then send the activation link. Completing both steps advances the case to patient appointment update.',
    'start-monitoring':
        'The practice was asked to start monitoring but has not yet. Pick the quickstart, set the aligner numbers, and start DM monitoring on their behalf to advance the case to treatment in progress.',
    'schedule-in-office-appointment':
        'The practice was asked to schedule an in-office appointment. When they share the date (usually in the chat), schedule it on their behalf; the case exits the hold and resumes its pre-hold step.',
    'view-treatment': 'View the treatment-in-progress case details.',
    'manage-virtual-appointments':
        "The patient's open VAs (open, waiting on patient or doctor) across DM and Clinical. The case leaves this step automatically once none remain open.",
    [FINAL_REVIEW_TASK_ID]:
        'Check the design and Dx together before they go to the GP (V2 cases: to the orthodontist first); approve, or hand back with notes on the design or Dx sections.',
    'ortho-review':
        "Review the design and the published Dx on the orthodontist's behalf: approve them for the patient, or send feedback notes on the design or Dx back to Clinical.",
    'refinement-go-ahead':
        "Decide the refinement on the orthodontist's behalf, from ODO's trigger notes: approve it, continue treatment, or start retention instead.",
    'retention-go-ahead':
        "Decide retention on the orthodontist's behalf, from ODO's trigger notes: start retention, continue treatment, or start a refinement instead.",
    'waiting-on-manufacturer':
        'Spark is working on the design. When you receive it from the manufacturer, mark the plan received to advance the case to review.',
    'refinement-waiting-on-manufacturer':
        'Spark is working on the refinement design. When you receive it, mark the design received to advance the case (to review, or straight to RDI if pre-approved).',
    'refinement-refusal':
        'The doctor refused the recommended refinement. Review the reason; resume treatment to return the case to monitoring.',
    'retention-refusal':
        'The doctor refused the recommended retention. Review the reason; resume treatment to return the case to monitoring.',
    'submit-retention-manufacturing':
        'The retention records are approved. Place the retainer order with the manufacturer, then mark it submitted to advance the case to manufacturing.',
    'submit-post-approval-redesign':
        'Review the finalized design, patient info, and Dx. Submit post-approval changes to Spark if the doctor requested any, or proceed straight to SDI creation.',
    'submit-sdi-manufacturing':
        'Review the finalized design and the SDI, then submit the case in the Spark portal. Mark as submitted once it is created with the manufacturer.',
    'review-sdi':
        "Review the latest SDI on the orthodontist's behalf and approve it for manufacturing. To change it first, open the SDI and create a new version.",
    'review-post-approval-redesign':
        "Review the manufacturer's post-approval redesign against the submitted changes. Approve to create the SDI, or reject with new instructions to send it back.",
    [APPROVAL_REVIEW_TASK_ID]:
        "Review the Dx report and decide on the practice's behalf: decline the case, or approve it with an aligner option and OrthoPulse selection.",
    [SDI_REVIEW_TASK_ID]:
        'View-only: the practice confirms aligner delivery on their own. Review the SDI while the case waits on them.',
    [RDI_REVIEW_TASK_ID]:
        'View-only: the practice confirms refinement delivery on their own. Review the RDI while the case waits on them.'
};

export function getTaskDescription(taskId: string): string | null {
    return TASK_DESCRIPTIONS[taskId] ?? null;
}

// Frontend-only TaskSurface heading, for when it must differ from the task's button label
// (e.g. approval-review's button says "View", its heading "Pending approval").
const TASK_TITLES: Record<string, string> = {
    'view-submission': 'Submission incomplete',
    [APPROVAL_REVIEW_TASK_ID]: 'Pending approval',
    [SDI_REVIEW_TASK_ID]: 'Pending patient appointment update',
    [RDI_REVIEW_TASK_ID]: 'Pending patient appointment update'
};

export function getTaskTitle(taskId: string, fallbackLabel: string): string {
    return TASK_TITLES[taskId] ?? fallbackLabel;
}

// Frontend-only marker beside the TaskSurface title, e.g. a case returned from Final review.
const TASK_TITLE_ACCESSORIES: Record<string, ComponentType<{ patientId: string }>> = {
    'submit-design-plan': HandBackChip,
    'review-design-finalize-dx': HandBackChip,
    [FINAL_REVIEW_TASK_ID]: HandBackChip
};

export function getTaskTitleAccessory(taskId: string): ComponentType<{ patientId: string }> | null {
    return TASK_TITLE_ACCESSORIES[taskId] ?? null;
}
